import { productAlt } from '../../server/store-collections.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Check, ChevronLeft, Clock, PackageCheck } from 'lucide-react';
import LazyModelViewer from '../LazyModelViewer.jsx';
import BuyBox from './BuyBox.jsx';
import BackgroundFilm from './BackgroundFilm.jsx';
import Launch from './Launch.jsx';
import { ProductTile } from './Home.jsx';
import { categoryName, gridCols, isVideo, photoAt, priceTag } from './api.js';
import { useChosenOption } from './commerce.js';
import { Picture, Reveal } from './ui.jsx';

// What a product page shows, worked out once from the product: its photos, and its detail sections in a fixed,
// tidy layout (title, text, list, picture) whatever shape they were entered in.
export function productContent(product) {
  const config = product.viewer_config || {};
  const blocks = (Array.isArray(config.product_blocks) ? config.product_blocks : []).filter((block) => block && !block.hidden && block.type !== 'divider');
  const galleryUrls = (product.gallery || []).map((item) => typeof item === 'string' ? item : item?.url || item?.image).filter(Boolean);
  const blockPhotos = blocks.filter((block) => block.type === 'gallery' || block.type === 'media').flatMap((block) => [block.image, block.media_url, ...(Array.isArray(block.items) ? block.items.map((item) => typeof item === 'string' ? item : item?.url || item?.image) : [])])
    .filter((path) => typeof path === 'string' && path.startsWith('/'));
  const media = [...new Set([product.image, config.hero_background_url, ...galleryUrls, ...blockPhotos].filter(Boolean))];
  const details = blocks.filter((block) => block.type !== 'gallery' && block.type !== 'media' && (block.title || block.text || (block.items || []).length)).map((block) => ({
    id: block.id || block.title, title: block.title || '', text: block.text || '',
    items: (Array.isArray(block.items) ? block.items : []).filter((item) => typeof item === 'string' && item.trim()).map((item) => item.replace(/^\s*(?:[-*•]+|\d+[.)-]?)\s*/, '')),
    ordered: block.type === 'install' || /install|step|how to/i.test(block.title || ''),
    warn: /warning|caution|important|before you buy/i.test(block.title || ''),
    image: [block.image, block.media_url].find((path) => typeof path === 'string' && path.startsWith('/') && !isVideo(path)) || '',
  }));
  return { media, photos: media.filter((path) => !isVideo(path)), videos: media.filter(isVideo), details };
}

// The 3D model or a photo, with small buttons to switch between them.
function Stage({ product, photos: allPhotos }) {
  // A chosen version with its own model: that model is the one on stage, with its photo first.
  const option = useChosenOption(product);
  const modelUrl = option?.model_url || product.model_url;
  const photos = useMemo(() => option?.model_url && option.image_url ? [option.image_url, ...allPhotos.filter((path) => path !== option.image_url)] : allPhotos, [allPhotos, option?.model_url, option?.image_url]);
  const hasModel = Boolean(modelUrl);
  const [view, setView] = useState(hasModel ? '3d' : 0);
  // Picking a choice (a hinge type, say) shows that choice's photo; the 3D button still turns its model.
  const picked = useRef(option?.id);
  useEffect(() => {
    if (picked.current === option?.id) return;
    picked.current = option?.id;
    const at = option?.image_url ? photos.indexOf(option.image_url) : -1;
    setView(at >= 0 ? at : hasModel ? '3d' : 0);
  }, [option?.id]);
  const config = product.viewer_config || {};
  return <div className="shop-stage">
    <div className="shop-stage-view">
      {view === '3d'
        ? <LazyModelViewer key={`${product.slug}:${modelUrl}`} modelUrl={modelUrl} config={config} showControls={false} showViewAngle={false} fallbackImageUrl={photoAt(option?.model_url && option.image_url ? option.image_url : product.image, 1080)} fallbackAlt={product.name} ariaLabel={`3D model of ${product.name}${option?.model_url ? `, ${option.label}` : ''}. Drag to turn it.`} />
        : <Picture key={photos[view]} src={photos[view]} alt={productAlt(product)} sizes="(max-width: 900px) 100vw, 760px" priority fit={/\.png(?:[?#]|$)/i.test(photos[view] || '') ? 'contain' : undefined} />}
      {view === '3d' && <span className="shop-stage-hint">Drag to turn · scroll or pinch to zoom</span>}
    </div>
    {(photos.length + (hasModel ? 1 : 0)) > 1 && <div className="shop-thumbs" role="group" aria-label="Views">
      {hasModel && <button type="button" aria-pressed={view === '3d'} onClick={() => setView('3d')} aria-label="3D model"><Box size={22} /></button>}
      {photos.map((path, index) => <button type="button" key={path} aria-pressed={view === index} onClick={() => setView(index)} aria-label={`Photo ${index + 1}`}><Picture src={path} alt="" sizes="76px" /></button>)}
    </div>}
  </div>;
}

export function ProductFacts({ product }) {
  const fitment = product.fitment || {};
  const stock = /^\d+$/.test(String(product.availability || '')) ? 'In stock' : product.availability;
  return <>
    <div className="shop-meta">
      {stock && product.purchase_availability === 'available' && <span><PackageCheck />{stock}</span>}
      {product.lead_time && <span><Clock />{product.lead_time}</span>}
    </div>
    {(fitment.platform || (fitment.generations || []).length > 0 || fitment.note) && <div className="shop-fit">
      <strong>Fits {[fitment.platform, (fitment.generations || []).join(', ')].filter(Boolean).join(' ') || 'your build'}</strong>
      {fitment.note && <p>{fitment.note}</p>}
    </div>}
  </>;
}

export function DetailCards({ details }) {
  if (!details.length) return null;
  return <div className="shop-details">
    {details.map((detail, index) => <Reveal key={detail.id || index} i={index % 3} className={`shop-detail ${detail.warn ? 'warn' : ''}`}>
      {detail.image && <Picture src={detail.image} alt="" sizes="(max-width: 700px) 100vw, 400px" />}
      {detail.title && <h3>{detail.title}</h3>}
      {detail.text && <p>{detail.text}</p>}
      {detail.items.length > 0 && (detail.ordered ? <ol>{detail.items.map((item) => <li key={item}>{item}</li>)}</ol> : <ul>{detail.items.map((item) => <li key={item}>{item}</li>)}</ul>)}
    </Reveal>)}
  </div>;
}

// The bar that keeps the price and the buy button in reach once the main buttons have scrolled away.
// It appears after `watch` has scrolled off the top, and steps aside again while `until` (a second set of buy
// buttons further down, if there is one) is on screen.
export function BuyBar({ product, payment, cart, onPrivacy, watch, until = null }) {
  const [past, setPast] = useState(false), [atEnd, setAtEnd] = useState(false);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const observers = [];
    const observe = (target, onChange) => { if (!target) return; const observer = new IntersectionObserver(([entry]) => onChange(entry), { threshold: 0 }); observer.observe(target); observers.push(observer); };
    observe(watch.current, (entry) => setPast(!entry.isIntersecting && entry.boundingClientRect.top < 0));
    observe(until?.current, (entry) => setAtEnd(entry.isIntersecting));
    return () => observers.forEach((observer) => observer.disconnect());
  }, [watch, until, product.slug]);
  const shown = past && !atEnd;
  return <div className={`shop-buybar ${shown ? 'is-shown' : ''}`} aria-hidden={!shown} inert={!shown}>
    <div className="shop-buybar-name"><strong>{product.name}</strong><span>{product.purchase_availability === 'beta' ? 'Coming soon' : priceTag(product.price_cents, product.currency)}</span></div>
    <BuyBox product={product} payment={payment} cart={cart} onPrivacy={onPrivacy} compact />
  </div>;
}

export default function ProductPage({ product, related = [], payment, cart, onBack, onOpen, onPrefetch, onPrivacy }) {
  const content = useMemo(() => productContent(product), [product]);
  const buy = useRef(null);
  const film = product.viewer_config?.background_film;
  const beta = product.purchase_availability === 'beta';
  if (product.launch_page) return <>
    {film && <BackgroundFilm key={product.slug} film={film} />}
    <Launch product={product} content={content} related={related} payment={payment} cart={cart} onBack={onBack} onOpen={onOpen} onPrefetch={onPrefetch} onPrivacy={onPrivacy} />
  </>;
  return <article className={film ? 'has-bgfilm' : undefined}>
    {film && <BackgroundFilm key={product.slug} film={film} />}
    <div className="shop-wrap">
      <div className="shop-product-top">
        <Stage product={product} photos={content.photos} />
        <div className="shop-buy">
          <button type="button" className="shop-link shop-back" onClick={onBack}>All products<ChevronLeft /></button>
          <span className="shop-eyebrow">{categoryName(product.category)}{product.collection ? ` · ${product.collection}` : ''}</span>
          <h1>{product.name}</h1>
          {product.subtitle && <p className="shop-buy-sub">{product.subtitle}</p>}
          {/* A beta product has no price on show yet, only the badge. */}
          <div className="shop-price">
            {!beta && <span>{priceTag(product.price_cents, product.currency)}</span>}
            {beta && <span className="shop-badge">Beta · coming soon</span>}
          </div>
          <ProductFacts product={product} />
          <div ref={buy}><BuyBox product={product} payment={payment} cart={cart} onPrivacy={onPrivacy} /></div>
        </div>
      </div>
    </div>
    {(product.description || (product.features || []).length > 0) && <section className="shop-section"><div className="shop-wrap shop-overview">
      <Reveal><span className="shop-eyebrow">Overview</span><p className="shop-overview-text" style={{ marginTop: 18 }}>{product.description}</p></Reveal>
      {(product.features || []).length > 0 && <Reveal as="ul" i={1} className="shop-features">{product.features.map((feature) => <li key={feature}><Check />{feature}</li>)}</Reveal>}
    </div></section>}
    {content.details.length > 0 && <section className="shop-section" style={{ paddingTop: 0 }}><div className="shop-wrap"><DetailCards details={content.details} /></div></section>}
    {content.photos.length > 1 && <section style={{ paddingBottom: 'clamp(72px, 11vw, 150px)' }}><div className="shop-wrap">
      {/* Photos sit inside the page, centred: two across (one across on phones), the first one wide when the count is odd. */}
      <div className={`shop-photos ${content.photos.length % 2 ? 'is-odd' : ''}`}>{content.photos.map((path) => <Picture key={path} src={path} alt={productAlt(product)} sizes="(max-width: 700px) 100vw, (max-width: 1240px) 50vw, 620px" />)}</div>
    </div></section>}
    {related.length > 0 && <section className="shop-section" style={{ paddingTop: 0 }}><div className="shop-wrap">
      <Reveal className="shop-section-head"><span className="shop-eyebrow">More from 3DLabs</span><h2 className="shop-title">You might also like.</h2></Reveal>
      <div className="shop-grid" style={{ '--cols': gridCols(related.length) }}>{related.map((item, index) => <ProductTile key={item.id} product={item} onOpen={onOpen} onPrefetch={onPrefetch} i={index} />)}</div>
    </div></section>}
    <BuyBar product={product} payment={payment} cart={cart} onPrivacy={onPrivacy} watch={buy} />
  </article>;
}
